import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { Home, ArrowLeft } from "lucide-react";

/**
 * Small floating "Back" and "Home" buttons that appear once the user has
 * scrolled down a page. The header/breadcrumb already provide a way back
 * to home at the TOP of the page, but on long tool/category pages that's
 * out of reach once scrolled down -- this keeps both actions reachable
 * from anywhere on the page without needing to scroll back up.
 *
 * Not rendered on the homepage itself (nothing to "go back to" there).
 */
export function FloatingNavButtons() {
  const [location] = useLocation();
  const [visible, setVisible] = useState(false);
  // On phones, step aside while a form field has focus so it never covers the field.
  const [fieldFocused, setFieldFocused] = useState(false);
  useEffect(() => {
    const isField = (t: EventTarget | null) =>
      t instanceof HTMLElement && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) && !t.closest('[role="dialog"]');
    const on = (e: FocusEvent) => setFieldFocused(isField(e.target));
    const off = () => setFieldFocused(false);
    document.addEventListener("focusin", on);
    document.addEventListener("focusout", off);
    return () => { document.removeEventListener("focusin", on); document.removeEventListener("focusout", off); };
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Reset visibility on navigation so it doesn't briefly show on a fresh page
  useEffect(() => {
    setVisible(window.scrollY > 400);
  }, [location]);

  if (location === "/") return null;

  return (
    <div
      className={`max-sm:hidden ${fieldFocused ? "max-sm:opacity-0 max-sm:pointer-events-none " : ""}fixed bottom-6 right-6 max-sm:bottom-20 max-sm:right-3 z-40 flex flex-col gap-2 transition-all duration-200 ${
        visible
          ? "opacity-100 translate-y-0"
          : "opacity-0 translate-y-2 pointer-events-none"
      }`}
    >
      <button
        onClick={() => window.history.back()}
        aria-label="Go back"
        title="Go back"
        className="w-10 h-10 rounded-full bg-card border border-border shadow-md flex items-center justify-center hover:border-primary/40 hover:text-primary transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <Link
        href="/"
        aria-label="Back to homepage"
        title="Back to homepage"
        className="w-10 h-10 rounded-full bg-primary text-primary-foreground shadow-md flex items-center justify-center hover:opacity-90 transition-opacity"
      >
        <Home className="h-4 w-4" />
      </Link>
    </div>
  );
}
